import { ExclamationCircleIcon, LockClosedIcon, ShoppingCartIcon } from '@heroicons/react/24/solid';
import { Alert, Button, Card, CardBody, CardFooter, CardHeader, Typography } from '@material-tailwind/react';
import { Autocomplete, Dialog, Icon, styled, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GetAllProducts, GetProductsWithImages } from '../../services/ApiServices/ProductService';
import { formatPrice } from '../../services/formatPrice';
import { Breadcrumb } from '../Breadcrumb';

function ProductComparison({ setLoading }: any) {
  const [product1, setProduct1] = useState({} as any);
  const [product2, setProduct2] = useState({} as any);
  const [allProducts, setAllProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentType, setCurrentType] = useState("");
  const [currentTypeControl, setCurrentTypeControl] = useState(0);
  const [openAlert, setOpenAlert] = useState(false);
  const [openSizeChartModal, setOpenSizeChartModal] = useState(false);
  const [currentSizeChart, setCurrentSizeChart] = useState('');
  const navigate = useNavigate();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const userInfos = await GetAllProducts();
      setLoading(false);
      setAllProducts(userInfos);
    } catch (error: any) {
      handleFetchError(error);
    }
  }

  const handleFetchError = (error: any) => {
    if (error.response && error.response.data && error.response.data.message) {
      setErrorMessage(error.response.data.message);
    } else {
      setErrorMessage('An error occurred. Please try again later.');
    }
    setLoading(false);
  }

  const fetchProduct1 = async (product: any) => {
    setLoading(true);
    try {
      if (currentType === "" || currentTypeControl === 1) {
        setCurrentType(product.type);
        setCurrentTypeControl(1);
        if (product2.type !== product.type) {
          setProduct2({});
        }
      } else if (currentType !== product.type) {
        setOpenAlert(true);
        setTimeout(() => {
          setOpenAlert(false);
        }, 3000);
        setProduct1({});
        setLoading(false);
        return;
      }
      const userInfos = await GetProductsWithImages(product.id);
      setLoading(false);
      setProduct1(userInfos);
    } catch (error: any) {
      handleFetchError(error);
    }
  }

  const fetchProduct2 = async (product: any) => {
    setLoading(true);
    try {
      if (currentType === "" || currentTypeControl === 2) {
        setCurrentType(product.type);
        setCurrentTypeControl(2);
        if (product1.type !== product.type) {
          setProduct1({});
        }
      } else if (currentType !== product.type) {
        setOpenAlert(true);
        setTimeout(() => {
          setOpenAlert(false);
        }, 3000);
        setLoading(false);
        setProduct2({});
        return;
      }
      const userInfos = await GetProductsWithImages(product.id);
      setLoading(false);
      setProduct2(userInfos);
    } catch (error: any) {
      handleFetchError(error);
    }
  }

  const handleOpenSizeChartModal = (sizeChartUrl: any) => {
    setCurrentSizeChart(sizeChartUrl);
    setOpenSizeChartModal(true);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'darkorange',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'darkorange',
    },
  });

  return (
    <>
      <div className="w-full p-4">
        <Breadcrumb text1="So sánh sản phẩm" link1="compare-products" />
        <div className="product-compare w-full flex justify-around mt-3">
          <div className="w-1/4">
            <div className="mb-4 ml-28" style={{fontWeight:"bold"}}>Chọn sản phẩm</div>
            <div className="mb-10">
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={allProducts}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option: any) => option.id}
                onChange={(event, newValue) => {
                  fetchProduct1(newValue);
                }}
                renderInput={(params) => <StyledTextField {...params} label="Sản phẩm" />}
              />
            </div>
            {!product1.productImages &&
              <Card placeholder={""} className="mt-6 w-96 animate-pulse">
                <CardHeader placeholder={""} className="relative grid h-56 place-items-center bg-gray-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-12 w-12 text-gray-500"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                    />
                  </svg>
                </CardHeader>
                <CardBody placeholder={""}>
                  <Typography placeholder={""} as="div" variant="h1" className="mb-4 h-3 w-56 rounded-full bg-gray-300">
                    &nbsp;
                  </Typography>
                  <Typography placeholder={""} as="div" variant="paragraph" className="mb-2 h-2 w-full rounded-full bg-gray-300">
                    &nbsp;
                  </Typography>
                </CardBody>
                <CardFooter placeholder={""} className="pt-0">
                  <Button placeholder={""} disabled tabIndex={-1} className="h-8 w-20 bg-gray-300 shadow-none hover:shadow-none">
                    &nbsp;
                  </Button>
                </CardFooter>
              </Card>
            }
            {product1.productImages && (
              <>
                <img src={product1.productImages[0].imageUrl} alt="" />
                <div className="w-full">
                  <Card placeholder={""}>
                    <CardBody placeholder={""} className="product-detail-body">
                      <h1 className="text-2xl font-semibold mb-3">{product1.name}</h1>
                      <div className="text-gray-500 mb-3 font-bold">Thương hiệu: {product1.brand ? product1.brand.name : ""}</div>
                      <div className="text-xl font-semibold text-red-500 mb-3">{formatPrice(product1.price)}</div>
                      <div className="text-gray-500 mb-3 font-bold">Loại sản phẩm: {product1.type}</div>
                      <div className="text-gray-500 mb-3 font-bold">Chất liệu: {product1.material}</div>
                      <div className="flex items-center mb-3 font-bold">
                        <div className="text-gray-500">Bảng size:</div>
                        <div className="ml-3">
                          <div className="product-size-select">
                            <p className="cursor-pointer text-blue-500" onClick={() => handleOpenSizeChartModal(product1.brand.sizeChart)}>
                              {product1.brand.sizeChart ? "Xem chi tiết" : "Không có size chart"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-5">
                        <div className="product-size-select">
                          <p>{product1.quantity} sản phẩm có sẵn</p>
                        </div>
                      </div>
                      <Button placeholder={""} variant="filled" className="flex justify-center gap-3 w-full mb-5 rounded-full" color="orange"
                        onClick={(e) => navigate("/product-detail/" + product1.id)}>
                        <ShoppingCartIcon fill="none" stroke="white" width={20} height={20} />
                        <span className="text-white">Mua ngay</span>
                      </Button>
                      <Dialog open={openSizeChartModal} onClose={() => setOpenSizeChartModal(false)}>
                        <div className="flex justify-center items-center p-4">
                          <img src={currentSizeChart} alt="Size Chart" className="max-w-full max-h-full" />
                        </div>
                      </Dialog>
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </div>
          <div className="w-1/4">
            <div className='mb-4 ml-28' style={{fontWeight:"bold"}}>Chọn sản phẩm</div>
            <div className="mb-10">
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={allProducts}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option: any) => option.id}
                onChange={(event, newValue) => {
                  fetchProduct2(newValue);
                }}
                renderInput={(params) => <StyledTextField {...params} label="Sản phẩm" />}
              />
            </div>
            {!product2.productImages && (
              <Card placeholder={""} className="mt-6 w-96 animate-pulse">
                <CardHeader placeholder={""} className="relative grid h-56 place-items-center bg-gray-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-12 w-12 text-gray-500"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                    />
                  </svg>
                </CardHeader>
                <CardBody placeholder={""}>
                  <Typography placeholder={""} as="div" variant="h1" className="mb-4 h-3 w-56 rounded-full bg-gray-300">
                    &nbsp;
                  </Typography>
                  <Typography placeholder={""} as="div" variant="paragraph" className="mb-2 h-2 w-full rounded-full bg-gray-300">
                    &nbsp;
                  </Typography>
                </CardBody>
                <CardFooter placeholder={""} className="pt-0">
                  <Button placeholder={""} disabled tabIndex={-1} className="h-8 w-20 bg-gray-300 shadow-none hover:shadow-none">
                    &nbsp;
                  </Button>
                </CardFooter>
              </Card>
            )}
            {product2.productImages && (
              <>
                <img src={product2.productImages[0].imageUrl} alt="" />
                <div className="w-full">
                  <Card placeholder={""}>
                    <CardBody placeholder={""} className="product-detail-body">
                      <h1 className="text-2xl font-semibold mb-3">{product2.name}</h1>
                      <div className="text-gray-500 mb-3 font-bold">Thương hiệu: {product2.brand ? product2.brand.name : ""}</div>
                      <div className="text-xl font-semibold text-red-500 mb-3">{formatPrice(product2.price)}</div>
                      <div className="text-gray-500 mb-3 font-bold">Loại sản phẩm: {product2.type}</div>
                      <div className="text-gray-500 mb-3 font-bold">Chất liệu: {product2.material}</div>
                      <div className="flex items-center mb-3 font-bold">
                        <div className="text-gray-500">Bảng size:</div>
                        <div className="ml-3">
                          <div className="product-size-select">
                            <p className="cursor-pointer text-blue-500" onClick={() => handleOpenSizeChartModal(product2.brand.sizeChart)}>
                              {product2.brand.sizeChart ? "Xem chi tiết" : "Không có size chart"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-5">
                        <div className="product-size-select">
                          <p>{product2.quantity} sản phẩm có sẵn</p>
                        </div>
                      </div>
                      <Button placeholder={""} variant="filled" className="flex justify-center gap-3 w-full mb-5 rounded-full" color="orange"
                        onClick={(e) => navigate("/product-detail/" + product2.id)}>
                        <ShoppingCartIcon fill="none" stroke="white" width={20} height={20} />
                        <span className="text-white">Mua ngay</span>
                      </Button>
                      <Dialog open={openSizeChartModal} onClose={() => setOpenSizeChartModal(false)}>
                        <div className="flex justify-center items-center p-4">
                          <img src={currentSizeChart} alt="Size Chart" className="max-w-full max-h-full" />
                        </div>
                      </Dialog>
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
        <Alert open={openAlert} onClose={() => setOpenAlert(false)} style={{
          position: "fixed",
          width: "45%",
          top: "5%",
          zIndex: "50",
          left: "56%"
        }}
          animate={{
            mount: { x: 0 },
            unmount: { x: 100 },
          }}
          className="transition-transform transform ease-in-out duration-300"
          color="red"
          icon={
            <Icon className="w-6 h-6">
              <ExclamationCircleIcon className="w-6 h-6" />
            </Icon>
          }
        >
          Sản phẩm khác loại không thể so sánh được!!!
        </Alert>
      </div>
    </>
  );
}

export default ProductComparison;
